import { useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react'
import { TrackEvent, type LocalVideoTrack } from 'livekit-client'
import {
  ChevronRight,
  Eye,
  LoaderCircle,
  Lock,
  Megaphone,
  Mic,
  MicOff,
  Power,
  Radio,
  RectangleHorizontal,
  ScreenShare,
  ShieldBan,
  UserPlus,
  UsersRound,
  Video,
  VideoOff,
  type LucideIcon,
} from 'lucide-react'
import { Badge, Button, ConfirmDialog, IconButton, Notice, Select, Skeleton, SkeletonGroup } from '@/components/play'
import { apiFailure } from '@/lib/apiFailure'
import { waitWords } from '@/lib/retryWait'
import { useWatchLayout } from '@/lib/watch/useMediaQuery'
import type { HostSources } from '@/lib/watch/useHostSources'
import type { StageDevice, StagePresence, StagePresenceError, StagePresenceErrorCode } from '@/lib/watch/useStagePresence'
import { cn } from '@/lib/utils'
import { InviteToStageOpener } from './HostInvites'
import { SourceVolume, type SourceVolumeProps } from './SourceVolume'
import { WatchSheet } from './WatchSheet'
import './watch.css'

/**
 * The stage this viewer is live on, when it isn't the one on screen: whose it is (`name`, "Shane"), and what goes back to
 * it. Without `onShow` (a stage the switcher doesn't list) it is a label alone; without a name, "another stage".
 */
export interface LiveOnStage {
  name: string | null
  onShow?(): void
}

/**
 * "Live on Shane’s stage" above a dock (the host's or a guest's), so its controls say which live they act on: a button
 * back to that stage, or a label alone.
 */
export function LiveOn({ stage }: { stage: LiveOnStage }) {
  const label = stage.name ? `Live on ${stage.name}’s stage` : 'Live on another stage'
  return stage.onShow ? (
    <Button variant="ghost" size="compact" className="watch-host__live-on" onClick={stage.onShow}>
      <Radio aria-hidden="true" />
      <span className="watch-host__live-on-label">{label}</span>
      <ChevronRight aria-hidden="true" />
    </Button>
  ) : (
    <Badge tone="quiet" icon={<Radio />} className="watch-host__live-on">
      {label}
    </Badge>
  )
}

export interface HostDockProps {
  /** The viewer may host this stage (the stage payload's `viewer.can_host`). Nothing renders otherwise. */
  canHost: boolean
  /** `useStagePresence()`'s result: the dock follows its state and calls its actions. */
  presence: StagePresence
  /** How many others are watching, for the camera check's line (the room's participants, less this tab). */
  audienceCount: number
  /**
   * The camera the host is looking at when it isn't the one their audience follows; null otherwise. The page sets
   * it when a host taps a camera (instead of a viewer's override) and puts `<PreviewLabel />` in the canvas's label
   * slot meanwhile.
   */
  previewIdentity: string | null
  /**
   * Show my audience: calls the previewed camera (`watchApi.callCamera`), then the page clears `previewIdentity`.
   * Return a promise to show progress; a rejection says it failed in the dock.
   */
  onCallCamera(identity: string): void | Promise<unknown>
  /**
   * What the idle Go live does instead of opening the check (`presence.check()`), so the page can run sign-in and the
   * username step first and then call `presence.check()` itself. Without it, Go live opens the check.
   */
  onGoLive?(): void
  /** Every seat is taken by someone else: Go live is held, with the reason under the dock. Ignored while live. */
  stageFull?: boolean
  /**
   * The stage on screen when this viewer may invite people on it (`viewer.can_invite`), with its seats taken (its live
   * members): Invite to stage follows Go live (idle) or the camera (live), opening "Invite to your stage". Null or
   * absent for a host live on another stage while watching this one.
   */
  invite?: { stageId: string; seatsTaken: number } | null
  /**
   * The host is live on another stage of the event than the one on screen (`LiveOn`): above the dock, "Live on Shane’s
   * stage", so the microphone, camera and End say which live they act on.
   */
  liveOn?: LiveOnStage | null
  /**
   * The host's own sources (`useHostSources`): while live, Studio mode (the camera becomes a 16:9 source rather than a
   * face) and, where the browser can, Share screen follow the camera. Absent, neither shows.
   */
  sources?: HostSources | null
  /**
   * The Graphics control (`GraphicsConsoleOpener`, docs/features/badger-cohost.md, The host console): the stage's
   * broadcast elements. Shown in the live row, after Share screen, only while the host is live; the page passes it only
   * while they're live on the stage on screen.
   */
  graphics?: ReactNode
  /**
   * The source under the host's voice, while they are live on the stage on screen and their audience follows a camera
   * or the Live show (docs/features/stage-volume.md): its control follows Invite to stage, before Show my audience, and
   * a change that didn't go through (`error`) says so under the dock. Absent or null, no control shows.
   */
  sourceVolume?: (SourceVolumeProps & { error: string | null }) | null
  /** Classes for the dock's wrapper, for the page to place it. */
  className?: string
}

/**
 * The host's controls on the watch screen. Idle: one Go live button, which opens the camera check (a local preview,
 * who will see it, device pickers when there is a choice), or calls `onGoLive` so the page can sign in first; held
 * while the stage is full. Live: microphone, camera, Studio mode and Share screen (`sources`; toggles, pressed while
 * on) and End (confirmed), with Reconnecting under them while the room reconnects (the audience can't see the host
 * meanwhile). While the host previews a camera their audience isn't watching, Show my audience calls it. While their
 * audience follows a camera or the Live show, the source volume sets how loud it plays under their voice (`SourceVolume`). Nothing says
 * live until LiveKit has published: joining shows as Go live busy in the check. For a host who may invite on the stage on screen (`invite`), Invite to stage follows Go live or the camera
 * and opens "Invite to your stage", the + face's panel (the prototype's dock).
 */
export function HostDock({
  canHost,
  presence,
  audienceCount,
  previewIdentity,
  onCallCamera,
  onGoLive,
  stageFull = false,
  invite = null,
  liveOn = null,
  sources = null,
  graphics = null,
  sourceVolume = null,
  className,
}: HostDockProps) {
  // The dock's first control (Go live, or the microphone while live): focus lands here when the check or End closes,
  // or when the control that had focus goes away (Show my audience did its job, the live controls went).
  const firstRef = useRef<HTMLButtonElement>(null)
  const goLiveRef = useRef<HTMLButtonElement>(null)
  const dockRef = useRef<HTMLDivElement>(null)
  /** The dock's control that last had focus, until focus moves somewhere else that is still there. */
  const focusedRef = useRef<HTMLElement | null>(null)
  const reasonId = useId()
  const [ending, setEnding] = useState(false)
  const [endFailure, setEndFailure] = useState(END_FAILED)
  const [calling, setCalling] = useState(false)
  const [callFailed, setCallFailed] = useState(false)
  const mountedRef = useRef(true)
  // A phone's dock fits one row with Show my audience only without Invite to stage (the + face still offers it).
  const narrow = useWatchLayout() !== 'desktop'
  const { state, error } = presence
  const live = state === 'live'
  const checkOpen = state === 'checking' || state === 'joining'

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  useEffect(() => setCallFailed(false), [previewIdentity])
  // A live that ended elsewhere closes End's confirmation for good, so the next live doesn't open it again.
  useEffect(() => {
    if (!live) setEnding(false)
  }, [live])
  // After every render: the control that had focus was removed and nothing else took it, so it goes to the dock.
  useLayoutEffect(() => {
    const focused = focusedRef.current
    if (!focused || focused.isConnected) return
    const active = document.activeElement
    if (active && active !== document.body) return
    focusedRef.current = firstRef.current
    firstRef.current?.focus()
  })

  if (!canHost) return null

  const onDockFocus = (event: FocusEvent<HTMLDivElement>) => {
    focusedRef.current = event.target
  }
  const onDockBlur = (event: FocusEvent<HTMLDivElement>) => {
    const target = event.target
    // Focus moved on by the host (the target is still there): forget it. A removed control keeps it, for the effect.
    queueMicrotask(() => {
      if (focusedRef.current === target && target.isConnected && !dockRef.current?.contains(document.activeElement)) {
        focusedRef.current = null
      }
    })
  }

  const callCamera = async () => {
    if (!previewIdentity || calling) return
    setCallFailed(false)
    const result = onCallCamera(previewIdentity)
    if (!result || typeof (result as Promise<unknown>).then !== 'function') return
    setCalling(true)
    try {
      await result
    } catch {
      if (mountedRef.current) setCallFailed(true)
    } finally {
      if (mountedRef.current) setCalling(false)
    }
  }

  const showMyAudience = previewIdentity ? (
    <Button variant={live ? 'primary' : 'secondary'} loading={calling} onClick={() => void callCamera()}>
      <Megaphone aria-hidden="true" />
      Show my audience
    </Button>
  ) : null

  // The prototype's dock button: the same panel as the + face, anchored here on the desktop. On a phone it gives way
  // while Show my audience is up, so End stays on the row and the label on one line (the + face still invites).
  const inviteButton = invite && !(narrow && previewIdentity) ? (
    <InviteToStageOpener
      stageId={invite.stageId}
      seatsTaken={invite.seatsTaken}
      trigger={
        <IconButton variant="ghost" label="Invite to stage">
          <UserPlus />
        </IconButton>
      }
    />
  ) : null

  const dockMessage = checkOpen
    ? null
    : callFailed
      ? 'We couldn’t switch your audience’s camera. Try again.'
      : (error && presenceWords(error)) || (live && sources?.error) || (live && sourceVolume?.error) || null
  const full = stageFull && !live

  return (
    <div className={cn('watch-host', className)}>
      {liveOn && <LiveOn stage={liveOn} />}
      <div
        ref={dockRef}
        className="watch-dock"
        role="group"
        aria-label="Host controls"
        data-state={state}
        onFocus={onDockFocus}
        onBlur={onDockBlur}
      >
        {live ? (
          <>
            <IconButton ref={firstRef} variant="ghost" label="Mute" aria-pressed={!presence.micOn} onClick={() => void presence.toggleMic()}>
              {presence.micOn ? <Mic /> : <MicOff />}
            </IconButton>
            <IconButton
              variant="ghost"
              label="Turn camera off"
              aria-pressed={!presence.cameraOn}
              onClick={() => void presence.toggleCamera()}
            >
              {presence.cameraOn ? <Video /> : <VideoOff />}
            </IconButton>
            {sources && (
              <IconButton variant="ghost" label="Studio mode" aria-pressed={sources.studio} onClick={() => void sources.toggleStudio()}>
                <RectangleHorizontal />
              </IconButton>
            )}
            {sources?.canShare && (
              <IconButton variant="ghost" label="Share screen" aria-pressed={sources.sharing} onClick={() => void sources.toggleShare()}>
                <ScreenShare />
              </IconButton>
            )}
            {graphics}
            {inviteButton}
            {sourceVolume && <SourceVolume {...sourceVolume} />}
            {showMyAudience}
            <IconButton variant="danger" label="End live" onClick={() => setEnding(true)}>
              <Power />
            </IconButton>
          </>
        ) : (
          <>
            <Button
              ref={firstRef}
              disabled={full}
              focusableWhenDisabled
              aria-describedby={full ? reasonId : undefined}
              onClick={() => (onGoLive ? onGoLive() : void presence.check())}
            >
              <Radio aria-hidden="true" />
              Go live
            </Button>
            {inviteButton}
            {showMyAudience}
          </>
        )}
      </div>

      {/* A visible badge rather than a tooltip, so it reads on touch; it describes the held Go live. */}
      {full && (
        <Badge id={reasonId} tone="quiet" icon={<UsersRound />} className="watch-host__reason">
          {STAGE_FULL}
        </Badge>
      )}

      {/* One polite live region, there before any message, so a message that appears (Reconnecting, Freeing your spot,
          no longer live, a failed toggle) is announced. Nothing inside is a second region. Reconnecting sits here rather
          than in the dock's row, which a phone has no room to widen; it goes once the live is back. Freeing your spot
          shows while End's refused seat is still being given back. */}
      <div className="watch-host__status" aria-live="polite" aria-atomic="true">
        {live && presence.reconnecting && (
          <Badge tone="warning" icon={<LoaderCircle />} className="watch-host__busy">
            Reconnecting…
          </Badge>
        )}
        {presence.freeing && (
          <Badge tone="quiet" icon={<LoaderCircle />} className="watch-host__busy">
            Freeing your spot…
          </Badge>
        )}
        {dockMessage && (
          <Notice tone="error" aria-live="off" className="watch-host__notice">
            {dockMessage}
          </Notice>
        )}
      </div>

      <ConfirmDialog
        open={ending && live}
        onOpenChange={setEnding}
        title="End your live?"
        description="Your audience stops seeing and hearing you. The cameras stay on."
        confirmLabel="End live"
        onConfirm={() =>
          presence.leave().catch((failure: unknown) => {
            setEndFailure(endFailureMessage(failure))
            throw failure
          })
        }
        failureMessage={endFailure}
        finalFocus={firstRef}
      />

      <WatchSheet
        open={checkOpen}
        onOpenChange={(open) => {
          if (!open) presence.cancel()
        }}
        title="Camera check"
        initialFocus={goLiveRef}
        finalFocus={firstRef}
        footer={
          <>
            <Button variant="ghost" onClick={presence.cancel}>
              Cancel
            </Button>
            <Button
              ref={goLiveRef}
              focusableWhenDisabled
              disabled={!presence.canGoLive}
              loading={state === 'joining' || error?.code === 'reconnecting'}
              onClick={() => void presence.goLive()}
            >
              <Radio aria-hidden="true" />
              Go live
            </Button>
          </>
        }
      >
        <CameraCheck presence={presence} audienceCount={audienceCount} />
      </WatchSheet>
    </div>
  )
}

const END_FAILED = 'We couldn’t end your live. Try again.'

/** A full stage, in the badge under the dock and wherever a refusal says it: one form. */
const STAGE_FULL = 'Stage full'

/** What the dock says for the presence's error: its message, except a full stage, which takes the badge's words. */
function presenceWords(error: StagePresenceError): string {
  return error.code === 'stage_full' ? STAGE_FULL : error.message
}

/**
 * Why End didn't end the live, for End's confirmation and the page's leave guard alike. Ending is throttled with going
 * live (30 a minute): a 429 says when to try again.
 */
export function endFailureMessage(failure: unknown) {
  const refused = apiFailure(failure)
  const wait = refused.status === 429 ? waitWords(refused.retryAfterMs) : null
  return wait ? `That’s a lot of tries. Try again in ${wait}.` : END_FAILED
}

/** What the preview shows when the camera didn't open, by cause; anything else is No camera. */
const NO_PICTURE: Partial<Record<StagePresenceErrorCode, { Icon: LucideIcon; label: string }>> = {
  media_insecure: { Icon: Lock, label: 'Not a secure connection' },
  media_denied: { Icon: ShieldBan, label: 'Camera and microphone blocked' },
  mic_missing: { Icon: MicOff, label: 'No microphone' },
  media_missing: { Icon: VideoOff, label: 'No camera or microphone' },
  media_busy: { Icon: VideoOff, label: 'Camera or microphone in use' },
}

/** Opening again can't help: only another address or browser can. */
const NO_RETRY = new Set<StagePresenceErrorCode>(['media_insecure', 'media_unsupported'])

/** The check's body: the preview with its mute toggles, the pickers, who will see it, and what went wrong. */
function CameraCheck({ presence, audienceCount }: { presence: StagePresence; audienceCount: number }) {
  const { camera, cameraOn, micOn, cameras, microphones, cameraId, microphoneId, error } = presence
  const opening = camera === null && error === null
  const noPicture = (!camera && error && NO_PICTURE[error.code]) || { Icon: VideoOff, label: camera ? 'Your camera is off' : 'No camera' }
  return (
    <div className="watch-check">
      <div className="watch-check__preview">
        {opening ? (
          <SkeletonGroup label="Starting your camera" className="watch-check__fill">
            <Skeleton shape="media" className="watch-check__fill" />
          </SkeletonGroup>
        ) : camera && cameraOn ? (
          <div role="img" aria-label="Your camera" className="watch-check__fill">
            <LocalVideo track={camera} />
          </div>
        ) : (
          // No picture: the camera is off, or it never opened (its glyph says why). Never a frozen frame.
          <div role="img" aria-label={noPicture.label} className="watch-check__fill watch-check__off">
            <noPicture.Icon aria-hidden="true" />
          </div>
        )}
        {camera && (
          <div className="watch-check__tools">
            <IconButton variant="ghost" elevated label="Mute" aria-pressed={!micOn} onClick={() => void presence.toggleMic()}>
              {micOn ? <Mic /> : <MicOff />}
            </IconButton>
            <IconButton variant="ghost" elevated label="Turn camera off" aria-pressed={!cameraOn} onClick={() => void presence.toggleCamera()}>
              {cameraOn ? <Video /> : <VideoOff />}
            </IconButton>
          </div>
        )}
      </div>

      {(cameras.length > 1 || microphones.length > 1) && (
        <div className="watch-check__devices">
          {cameras.length > 1 && (
            <Select
              label="Camera"
              hideLabel
              icon={<Video />}
              items={deviceItems(cameras)}
              value={cameraId}
              onValueChange={(deviceId) => deviceId && void presence.selectCamera(deviceId)}
            />
          )}
          {microphones.length > 1 && (
            <Select
              label="Microphone"
              hideLabel
              icon={<Mic />}
              items={deviceItems(microphones)}
              value={microphoneId}
              onValueChange={(deviceId) => deviceId && void presence.selectMicrophone(deviceId)}
            />
          )}
        </div>
      )}

      <p className="watch-check__audience">
        <Eye aria-hidden="true" />
        {audienceLine(audienceCount)}
      </p>

      {error?.code === 'reconnecting' ? (
        // Not a failure: going live carries on by itself once the new connection joins.
        <Notice>{presenceWords(error)}</Notice>
      ) : error ? (
        <Notice
          tone="error"
          alert
          action={
            camera || NO_RETRY.has(error.code) ? undefined : (
              <Button variant="secondary" size="compact" onClick={() => void presence.check()}>
                Try again
              </Button>
            )
          }
        >
          {presenceWords(error)}
        </Notice>
      ) : null}
    </div>
  )
}

/**
 * The local camera, muted (the microphone is never played back), mirrored like a face unless it faces away (a phone's
 * rear camera reads the right way round). Rechecked when the track restarts on another device.
 */
function LocalVideo({ track }: { track: LocalVideoTrack }) {
  const ref = useRef<HTMLVideoElement>(null)
  const [mirrored, setMirrored] = useState(() => facesUser(track))
  useEffect(() => {
    const element = ref.current
    if (!element) return
    track.attach(element)
    return () => {
      track.detach(element)
    }
  }, [track])
  useEffect(() => {
    const update = () => setMirrored(facesUser(track))
    update()
    track.on(TrackEvent.Restarted, update)
    return () => {
      track.off(TrackEvent.Restarted, update)
    }
  }, [track])
  return (
    <video
      ref={ref}
      className={cn('watch-check__video', mirrored && 'watch-check__video--mirrored')}
      muted
      playsInline
      autoPlay
      disablePictureInPicture
    />
  )
}

const facesUser = (track: LocalVideoTrack) => track.mediaStreamTrack?.getSettings?.().facingMode !== 'environment'

const deviceItems = (devices: StageDevice[]) => devices.map((device) => ({ value: device.deviceId, label: device.label }))

function audienceLine(count: number) {
  if (count <= 0) return 'No one’s watching yet'
  return `${count.toLocaleString('en')} watching will see and hear you`
}

/**
 * The canvas's label while the host previews a camera their audience isn't watching (WatchCanvas's `label` slot).
 * A filled badge so it reads over video. A plain label, not a control: assistive technology reads "only you see
 * this" with it, and nothing hides behind a hover a phone doesn't have.
 */
export function PreviewLabel({ className }: { className?: string }) {
  return (
    <Badge tone="selection" icon={<Eye />} className={cn('watch-preview-label', className)}>
      Preview<span className="sr-only">, only you see this</span>
    </Badge>
  )
}
